import React from "react";
import ReactDOM from "react-dom/client";
import { ClerkProvider } from "@clerk/clerk-react";
import * as Sentry from "@sentry/react";
import "@/index.css";
import App from "@/App";

// ---------------------------------------------------------------------------
// Sentry — error monitoring (browser)
// ---------------------------------------------------------------------------
// DSN read from env. If not set (dev/preview), Sentry stays disabled.
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || 'production',
    // 10% trace sampling — enough signal, well under the free tier.
    tracesSampleRate: 0.1,
    // No session replay — would capture screen contents incl. financial data.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    // Strip PII. Cash Forth never lets financial data or emails leave this stack.
    sendDefaultPii: false,
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    // Drop events from extension-injected errors and other noise.
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
    ],
  });
}

const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

// Check if we have a valid Clerk key (must be a real key, not placeholder)
// Valid format: pk_test_<base64chars> or pk_live_<base64chars>
// Placeholder detection: contains XXXX or is empty
const isValidClerkKey = clerkPubKey 
  && clerkPubKey.startsWith('pk_') 
  && !clerkPubKey.includes('XXXX')
  && clerkPubKey.length > 20;

const root = ReactDOM.createRoot(document.getElementById("root"));

// Render with or without Clerk based on key validity
if (isValidClerkKey) {
  root.render(
    <React.StrictMode>
      <Sentry.ErrorBoundary fallback={<div style={{padding:'2rem',textAlign:'center'}}>Something went wrong. Please refresh the page.</div>}>
        <ClerkProvider publishableKey={clerkPubKey}>
          <App />
        </ClerkProvider>
      </Sentry.ErrorBoundary>
    </React.StrictMode>,
  );
} else {
  // Development/placeholder mode - render without Clerk
  console.warn('Cash Forth: Running in development mode without authentication. Add valid Clerk keys for production.');
  root.render(
    <React.StrictMode>
      <Sentry.ErrorBoundary fallback={<div style={{padding:'2rem',textAlign:'center'}}>Something went wrong. Please refresh the page.</div>}>
        <App devMode={true} />
      </Sentry.ErrorBoundary>
    </React.StrictMode>,
  );
}
